
import TooltipWithShowMoreOption from '@/components/common/TooltipWithShowMoreOption.vue';
import { Agent, Agent_Status, Agent_Type, useCommonDataQuery, useLicensePageQuery, useSettingsPageLinuxQuery } from '@/graphql/codegen/generated/graphql';
import { defineComponent, inject, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import AgentMessagesTooltip from '@/components/agents-table/AgentMessagesTooltip.vue';
import { useAgentRowStyle } from '@/views/agents/components/agents-table/agents-table.helpers';
import AgentStatusLegend from '@/views/agents/components/agents-table/components/agent-status-legend.vue';
import { IncrediTooltip } from 'incredi-ui';
import { AgentStatusMeta } from '@/views/agents/components/agents-table/types/agents-table.types';
import COLORS from '@/design-system/colors.json';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import { useOsService } from '@/services/OsService/OsService';
import { useAgentsTableUtils } from '@/views/agents/components/agents-table/agent-table-list/OS/agents-table.utils';
import { Constants } from '@/const/globals';

export default defineComponent({
  components: { IncrediTooltip, AgentStatusLegend, TooltipWithShowMoreOption, AgentMessagesTooltip },
  props: {
    agentRowStyle: {
      required: true,
      type: Object as PropType<ReturnType<ReturnType<typeof useAgentRowStyle>['getAgentRowStyle']>>,
    },
    agentData: { type: Object as PropType<Agent>, required: true },
  },
  setup() {
    const { t } = useI18n();
    const coordinatorsService = inject(IKCoordinatorsService);
    const osService = useOsService();
    let GQL_useSettingsPageLinuxQuery = null;
    if (osService?.isUnix.value) {
      GQL_useSettingsPageLinuxQuery = useSettingsPageLinuxQuery(() => ({ coordinatorId: coordinatorsService?.selectedCoordinator.value || '' }));
    }
    const GQL_CommonData = useCommonDataQuery(() => ({ coordinatorId: coordinatorsService?.selectedCoordinator.value || '' }));
    const { getAgentSettingsUrl } = useAgentsTableUtils();
    const GQL_userLicenseInfoQuery = useLicensePageQuery(() => ({ coordinatorId: coordinatorsService?.selectedCoordinator.value || '' }));
    return { t, osService, COLORS, GQL_useSettingsPageLinuxQuery, GQL_CommonData, GQL_userLicenseInfoQuery, getAgentSettingsUrl };
  },
  data() {
    const { t } = useI18n();
    return {
      showTooltip: false,
      showStatusTooltip: false,
      localLicenseTooltip: t('agentsTable.noRole'),
      agentNameClassConfigs: {
        offline: 'offline',
        incompatibleDuplicate: 'incompatible-duplicate-agent',
        default: 'default',
      },
      agentErrorIcons: {
        updating: '',
        duplicate: 'incredicon-duplicated-error text-System/app_error',
        incompatible: 'incredicon-solid-arrow-up-square text-System/app_error',
        warning: 'uis uis-exclamation-circle text-System/app_warning',
        error: 'uis uis-times-circle text-System/app_error',
      },
      isOverFlow: false,
      maxItems: 5,
    };
  },
  computed: {
    isSUVM() {
      return this.agentData?.metadata?.agentType === Agent_Type.SingleUse || this.agentData?.metadata?.agentType === Agent_Type.ExpiredSingleUse;
    },
    isSCL() {
      return this.GQL_userLicenseInfoQuery?.result?.value?.ibManagerEntities?.license?.windows?.features?.productFeatures?.includes(Constants.LICENSE_FEATURES.SCL);
    },
    chipStyle() {
      const chipStyle = {
        color: this.agentData.metadata?.isOffline ? COLORS['Text/text_low'] : COLORS['Text/text_medium'],
      };
      return { ...(this.agentRowStyle as Object), ...chipStyle };
    },
    agentStatus() {
      const { status } = this.agentData;

      if (this.agentData.metadata?.isIncompatibleVersion) {
        if (status === Agent_Status.Updating) {
          return status;
        }
        // Offline incompatible agent will display an Offline status instead of incompatible status.
        if (this.agentData.metadata?.isOffline) {
          return status;
        }
        return AgentStatusMeta.Incompatible;
      }
      if (this.agentData.metadata?.isDuplicateLicense) {
        return AgentStatusMeta.Duplicate;
      }

      return status;
    },
    isAgentHelping() {
      return this.agentData.status && this.agentData.status === Agent_Status.Helping;
    },
    isAgentInitiating() {
      return this.agentData.status && this.agentData.status === Agent_Status.Initiating;
    },
    isUpdatingAgent() {
      return this.agentData.status && this.agentData.status === Agent_Status.Updating;
    },

    getStatusIconName() {
      const agentMessages = this.agentData.warnings || '';
      const agentErrors = this.agentData.errors || '';
      if (this.isUpdatingAgent) {
        return this.agentErrorIcons.updating;
      }
      if (this.agentData.metadata?.isDuplicateLicense) {
        return this.agentErrorIcons.duplicate;
      }
      if (this.agentData.metadata?.isIncompatibleVersion) {
        return this.agentErrorIcons.incompatible;
      }
      if (agentErrors !== '') {
        return this.agentErrorIcons.error;
      }
      if (agentMessages !== '') {
        return this.agentErrorIcons.warning;
      }
      return false;
    },
    getNumOfBuildsText() {
      const count = this.getNumOfRunningBuilds;
      if (count === 1) {
        return this.agentData.runningBuildsTitles?.[0] || '';
      }
      return this.t('agentsTable.runningBuilds', { count });
    },
    getHelperCoresText() {
      if (this.getNumOfHelpedAgents === 1) return this.agentData.workingFor;
      if (this.getNumOfHelpedAgents > 1) {
        return `${this.getNumOfHelpedAgents} ${this.t('agentsTable.agents')}`;
      }
      return '';
    },
    getCoresCount() {
      if (!this.agentData.metadata?.isCloud && this.agentData.performance?.cpuLogical !== this.agentData.license?.helperCores) {
        return this.agentData.license?.helperCores;
      }
      return this.agentData.performance?.cpuLogical;
    },
    getNumOfRunningBuilds() {
      return this.agentData.runningBuildsTitles?.length || 0;
    },
    getNumOfHelpedAgents() {
      return this.agentData.workingFor?.split(',').length || 0;
    },
    helperLicenseTooltip() {
      if (this.agentData.metadata?.isCloud) {
        return 'Cloud helper';
      }
      if (this.agentData.metadata?.isHelper) {
        if (this.osService?.isUnix.value) {
          return this.isSUVM ? 'SUVM helper' : 'Helper';
        }
        if (this.agentData.license?.helperType === 'Fixed') {
          return 'Fixed helper';
        }
        return 'Floating helper';
      }
      return '';
    },
    getInitiatorType() {
      if (this.agentData.metadata?.isInitiator && this.agentData.license?.initiatorType) {
        return this.agentData.license?.initiatorType;
      }
      if (this.agentData.metadata?.isCIInitiator && this.agentData.license?.ciInitiatorType) {
        return this.agentData.license?.ciInitiatorType;
      }
      return undefined;
    },
    initiatorLicenseTooltip() {
      const initiatorType = this.getInitiatorType;
      const initiatorTypeText = this.agentData.metadata?.isInitiator ? 'initiator' : 'CI initiator';
      if (this.osService?.isUnix.value) {
        return this.isSUVM ? 'SUVM initiator' : 'Initiator';
      }
      switch (initiatorType) {
        case 'Fixed':
          if (this.agentData.metadata?.isBuildAvoidance) {
            return `Fixed ${initiatorTypeText} with Build cache license`;
          }
          return `Fixed ${initiatorTypeText}`;
        case 'Floating':
          if (this.agentData.metadata?.isBuildAvoidance) {
            if (this.agentData.license?.initiatorQueued) {
              return `Floating ${initiatorTypeText} with Build cache license (pending allocation)`;
            }
            return `Floating ${initiatorTypeText} with Build cache license`;
          }
          if (this.agentData.license?.initiatorQueued) {
            return `Floating ${initiatorTypeText} (pending allocation)`;
          }
          return `Floating ${initiatorTypeText}`;
        default:
          return '';
      }
    },
    fixedOrFloatInitiatorIcon() {
      const initiatorType = this.getInitiatorType;
      switch (initiatorType) {
        case 'Fixed':
          return 'icon-Fixed';

        case 'Floating':
          return 'incredicon-floating';

        case 'None':
          return '';
        default:
          return '';
      }
    },
    helperIconComputed() {
      if (this.agentData.metadata?.isCloud) {
        return 'icon-cloud';
      }
      if (this.agentData.metadata?.isHelper) {
        if (this.agentData.license?.helperType === 'Fixed') {
          return 'icon-Fixed';
        }
        return 'incredicon-floating';
      }
      return '';
    },
    helperClasses() {
      let classes = '';
      if (this.agentData.metadata?.isOffline) return 'offline';
      if (this.agentData.metadata?.isHelperDisabled) {
        if (this.agentStatus === Agent_Status.Stopped) return 'offline';
        return 'chip-disabled';
      }
      if (this.agentData.metadata?.isDuplicateLicense) classes += ' duplicate';
      if (this.agentData.metadata?.isCloud) classes += ' chip-cloud';
      return classes;
    },
    initiatorClasses() {
      let classes = '';
      if (this.agentData.metadata?.isOffline) return 'offline';
      if (this.agentData.metadata?.isDuplicateLicense) classes += ' duplicate';
      if (this.agentData.metadata?.isCloud) classes += ' chip-cloud';
      if (this.agentData.metadata?.isInitiatorPending || this?.isCIInitiatorPending(this.agentData)) classes += ' pending';
      return classes;
    },
    agentNameClass() {
      if (this.agentData.metadata?.isDuplicateLicense || (this.agentData.metadata?.isIncompatibleVersion && !this.agentData.metadata?.isOffline && this.agentData.status !== Agent_Status.Updating)) {
        return this.agentNameClassConfigs.incompatibleDuplicate;
      }
      if (this.agentData.metadata?.isOffline || this.agentData.status === Agent_Status.Stopped) {
        return this.agentNameClassConfigs.offline;
      }
      return this.agentNameClassConfigs.default;
    },
  },
  methods: {
    isCIInitiatorPending(agent: Agent) {
      return !!(
        agent &&
        agent.license &&
        agent.license.ciInitiatorType &&
        agent.license.ciInitiatorType !== 'None' &&
        agent.license.initiatorType === 'None' &&
        agent.license.initiatorQueued === true
      );
    },
    isAgentCloud() {
      return this.agentData.metadata?.isCloud;
    },
    openAgentSettings() {
      const url = this.getAgentSettingsUrl(this.agentData);
      const win = window.open(url, '_blank');
      win?.focus();
    },
    clickHandler(event: MouseEvent) {
      if (this.getNumOfRunningBuilds > 1 || this.isOverFlow || this.getNumOfHelpedAgents > 1) {
        event.stopPropagation();
        this.showTooltip = true;
      }
    },
  },
});
